import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDescriptionForPath } from '../../config/configEntry';
import { uploadBotAsset } from '../../services/admin/botAdminApi';
import { clearLocalBotIcon, saveLocalBotIcon } from './botIconLocalStorage';
import classes from './BotIconUploader.module.css';

const MAX_SVG_BYTES = 200 * 1024;
const MAX_PNG_BYTES = 500 * 1024;

export default function BotIconUploader({
  domainName,
  botName,
  fieldKey = 'botIconUrl',
  iconUrl,
  originalIconUrl,
  onIconUrlChange,
  descriptions,
  uploadButtonText,
  previewFallbackLabel,
}) {
  const { texts } = useSelector((state) => state.translate);
  const resolvedUploadButtonText = uploadButtonText ?? texts.uploadImageSvgPng;
  const resolvedPreviewFallbackLabel = previewFallbackLabel ?? texts.imageFallback;
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  const label = getDescriptionForPath(descriptions, fieldKey, fieldKey);

  const displayUrl = (iconUrl ?? '').trim();
  const originalUrl = (originalIconUrl ?? '').trim();
  const showRemove = Boolean(displayUrl) && displayUrl !== originalUrl;

  const onUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const resolveUploadFileName = (isSvg) => {
    if (fieldKey === 'botIconUrl') {
      return isSvg ? `${botName}.svg` : `${botName}.png`;
    }
    if (fieldKey === 'subjectIconUrl') {
      return `${domainName}.svg`;
    }
    return isSvg ? `${botName}_${fieldKey}.svg` : `${botName}_${fieldKey}.png`;
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadError('');

    const name = String(file.name || '').toLowerCase();
    const isSvg = name.endsWith('.svg') || file.type === 'image/svg+xml';
    const isPng = name.endsWith('.png') || file.type === 'image/png';
    const subjectIconOnly = fieldKey === 'subjectIconUrl';

    if (subjectIconOnly) {
      if (!isSvg) {
        setUploadError(texts.uploadOnlySvg);
        return;
      }
    } else if (!isSvg && !isPng) {
      setUploadError(texts.uploadOnlySvgOrPng);
      return;
    }

    if (isSvg) {
      if (typeof file.size === 'number' && file.size > MAX_SVG_BYTES) {
        setUploadError(texts.fileTooLarge200kb);
        return;
      }

      let svgText = '';
      try {
        svgText = await readFileAsText(file);
      } catch {
        setUploadError(texts.fileReadFailed);
        return;
      }

      const normalizedSvg = svgText.replace(/^\uFEFF/, '').trim();
      if (!/<svg[\s>]/i.test(normalizedSvg)) {
        setUploadError(texts.invalidSvgFile);
        return;
      }
    } else if (typeof file.size === 'number' && file.size > MAX_PNG_BYTES) {
      setUploadError(texts.fileTooLarge500kb);
      return;
    }

    if (!domainName || (!subjectIconOnly && !botName)) {
      setUploadError(texts.saveJsonFailed);
      return;
    }

    const previousUrl = displayUrl;
    const fileName = resolveUploadFileName(isSvg);
    const mimeType = isSvg ? 'image/svg+xml' : (file.type || 'image/png');

    let dataUrl = '';
    try {
      dataUrl = await readFileAsDataUrl(file);
    } catch {
      setUploadError(texts.fileReadFailed);
      return;
    }

    saveLocalBotIcon(domainName, botName, { url: dataUrl, mimeType, fileName }, fieldKey);
    onIconUrlChange(dataUrl);

    setUploading(true);
    try {
      const res = await uploadBotAsset({ domainName, fileName, file });
      if (!res?.ok) {
        throw new Error('upload failed');
      }
      const data = await res.json().catch(() => null);
      const url = typeof data?.url === 'string' ? data.url.trim() : '';
      if (!url) {
        throw new Error('missing url');
      }
      // Keep local data URL in draft for immediate preview; bucket file is ready for save/export.
    } catch {
      clearLocalBotIcon(domainName, botName, fieldKey);
      onIconUrlChange(previousUrl);
      setUploadError(texts.saveJsonFailed);
    } finally {
      setUploading(false);
    }
  };

  const onRemove = () => {
    clearLocalBotIcon(domainName, botName, fieldKey);
    setUploadError('');
    onIconUrlChange(originalIconUrl ?? '');
  };

  return (
    <div className={classes.field}>
      <label className={classes.Label}>{label}</label>
      {displayUrl ?
        <div className={classes.previewRow}>
          <img src={displayUrl} alt="" />
          <span>{resolvedPreviewFallbackLabel}</span>
        </div>
        : null}
      <div className={classes.actions}>
        <button
          type="button"
          className={classes.Btn}
          onClick={onUploadClick}
          disabled={uploading}
        >
          {uploading ? texts.savingJsonToBucket : resolvedUploadButtonText}
        </button>
        {showRemove ?
          <button
            type="button"
            className={classes.Btn}
            onClick={onRemove}
            disabled={uploading}
          >
            {texts.removeLocalImage}
          </button>
          : null}
        <input
          ref={fileInputRef}
          type="file"
          accept={fieldKey === 'subjectIconUrl' ? 'image/svg+xml,.svg' : 'image/svg+xml,.svg,image/png,.png'}
          style={{ display: 'none' }}
          onChange={onUploadFile}
        />
      </div>
      {uploadError ? <p className={classes.hintError}>{uploadError}</p> : null}
    </div>
  );
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(file);
  });
}
